import { useState, useEffect, useCallback, useRef } from 'react'
import { useDialogueSimulatorStore } from '../../../../stores/dialogueSimulatorStore'
import { useDialogueSimulator } from '../../../../hooks/useDialogueSimulator'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { GeneratorContext } from './GeneratorContext'
import { SceneChangeModal } from './SceneChangeModal'
import { Button } from '@creanizer/ui'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@creanizer/ui'
import { Card, CardHeader, CardTitle, CardContent } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
// import { Avatar, AvatarFallback, AvatarImage } from '@creanizer/ui' // Not available yet
import { AlertCircle, Download, MessageSquare, Users, Settings, Plus, Trash2, RefreshCw, Send, Edit2, X, Play, Pause, Star, Keyboard, Upload, Image, MapPin } from 'lucide-react'
import { toast } from 'sonner'
import type { DialogueParticipant, CharacterRelationship, SceneSettings, DialogueTurn, DialogueContext } from '@creanizer/types'

export default function DialogueSimulator() {
  const dialogueStore = useDialogueSimulatorStore()
  const characterStore = useCharacterStore()
  const worldStore = useWorldBuilderStore()
  const {
    generateRoleplay,
    generateAuto,
    continueDialogue,
    regenerateLastTurn,
    // analyzeRelationship, // 未実装のため一時的に除外
    compressHistory,
    editDialogueTurn,
    deleteDialogueTurn,
    isGenerating,
    isContinuing,
    isCompressing,
    error
  } = useDialogueSimulator()

  const [mode, setMode] = useState<'roleplay' | 'auto'>('roleplay')
  const [activeTab, setActiveTab] = useState<'setup' | 'dialogue' | 'history'>('setup')
  const [playerMessage, setPlayerMessage] = useState('')
  const [autoTurns, setAutoTurns] = useState(5)
  const [generatorContext, setGeneratorContext] = useState<DialogueContext>({})
  const [customContext, setCustomContext] = useState<string>('')

  // GeneratorContextのstateを親コンポーネントで管理（タブ切り替えでリセットされないように）
  const [uploadedFiles, setUploadedFiles] = useState<{ id: string; name: string; content: string; size: number }[]>([])
  const [primaryWorldId, setPrimaryWorldId] = useState<string>('none')
  const [secondaryWorldId, setSecondaryWorldId] = useState<string>('none')
  const [dataMode, setDataMode] = useState<'selected' | 'none'>('selected')
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<Set<string>>(new Set())
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set())
  const [selectedEnemyIds, setSelectedEnemyIds] = useState<Set<string>>(new Set())
  const [selectedRelationshipIds, setSelectedRelationshipIds] = useState<Set<string>>(new Set())
  const [selectedPlotIds, setSelectedPlotIds] = useState<Set<string>>(new Set())
  const [selectedTitleIds, setSelectedTitleIds] = useState<Set<string>>(new Set())
  const [selectedNameIds, setSelectedNameIds] = useState<Set<string>>(new Set())
  const [selectedWikipediaIds, setSelectedWikipediaIds] = useState<Set<string>>(new Set())
  const [selectedImagePromptIds, setSelectedImagePromptIds] = useState<Set<string>>(new Set())
  const [selectedSkillIds, setSelectedSkillIds] = useState<Set<string>>(new Set())
  const [selectedDungeonIds, setSelectedDungeonIds] = useState<Set<string>>(new Set())
  const [selectedCommunityIds, setSelectedCommunityIds] = useState<Set<string>>(new Set())
  const [selectedSocietyIds, setSelectedSocietyIds] = useState<Set<string>>(new Set())
  const [selectedBuildingIds, setSelectedBuildingIds] = useState<Set<string>>(new Set())
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(new Set())
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(new Set())
  const [selectedSummaryIds, setSelectedSummaryIds] = useState<Set<string>>(new Set())
  const [editingTurnId, setEditingTurnId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [useGemini3Mode, setUseGemini3Mode] = useState(false)
  const [useGemini3FlashMode, setUseGemini3FlashMode] = useState(false)
  const [showMobDialog, setShowMobDialog] = useState(false)
  const [mobCharacter, setMobCharacter] = useState({
    name: '',
    role: '',
    description: ''
  })
  const [showPresetDialog, setShowPresetDialog] = useState(false)
  const [presetForm, setPresetForm] = useState({
    name: '',
    description: '',
    tags: ''
  })
  const [showPresetList, setShowPresetList] = useState(false)
  const [showSceneChangeModal, setShowSceneChangeModal] = useState(false)

  // シーン設定の状態
  const [sceneSetting, setSceneSetting] = useState<SceneSettings>({
    location: '',
    situation: '',
    mood: 'casual',
    timeOfDay: undefined,
    weather: undefined,
    backgroundInfo: undefined,
    objectives: undefined
  })
  
  // 詳細オプション設定
  const [dialogueOptions, setDialogueOptions] = useState({
    responseLength: 'medium' as 'short' | 'medium' | 'long' | 'none',
    includeActions: true,
    includeThoughts: false,
    emotionalIntensity: 5,
    naturalFlow: true,
    conflictLevel: 3,
    humorLevel: 3,
    dramaticTension: 5
  })

  // 参加者選択モーダル
  const [showCharacterPicker, setShowCharacterPicker] = useState(false)
  
  // 関係性編集モーダル
  const [editingRelationship, setEditingRelationship] = useState<{char1: string, char2: string} | null>(null)
  const [relationshipForm, setRelationshipForm] = useState<Partial<CharacterRelationship>>({})
  const [showAddRelationship, setShowAddRelationship] = useState(false)

  // 初期化
  useEffect(() => {
    if (!dialogueStore.activeSimulation && dialogueStore.currentParticipants.length > 0) {
      dialogueStore.createSimulation()
    }
  }, [])

  // activeSimulationが変更されたらローカルstateを更新
  useEffect(() => {
    if (dialogueStore.activeSimulation?.result?.sceneSettings) {
      console.log('Updating local scene settings from activeSimulation:', dialogueStore.activeSimulation.result.sceneSettings)
      setSceneSetting(dialogueStore.activeSimulation.result.sceneSettings)
    }
  }, [dialogueStore.activeSimulation])

  // キーボードショートカット
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + Enter: 送信/生成
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        if (!isGenerating && !isContinuing) {
          handleGenerate()
        }
      }
      // Ctrl/Cmd + Shift + C: 続きを生成
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'C') {
        e.preventDefault()
        if (!isContinuing && dialogueStore.activeSimulation) {
          handleContinue()
        }
      }
      // Ctrl/Cmd + E: エクスポート
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault()
        if (dialogueStore.activeSimulation) {
          dialogueStore.exportSimulation(dialogueStore.activeSimulation.id, 'text')
        }
      }
      // Tab: タブ切り替え
      if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        const tabs = ['setup', 'dialogue', 'history']
        const currentIndex = tabs.indexOf(activeTab)
        const nextIndex = (currentIndex + 1) % tabs.length
        if (document.activeElement?.tagName !== 'INPUT' && 
            document.activeElement?.tagName !== 'TEXTAREA') {
          e.preventDefault()
          setActiveTab(tabs[nextIndex] as any)
        }
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isGenerating, isContinuing, activeTab, dialogueStore.activeSimulation, playerMessage])

  // キャラクター追加
  const handleAddCharacter = (character: any) => {
    console.log('Adding character:', {
      id: character.id,
      name: character.result?.name || character.result?.basicInfo?.name || character.title,
      hasResult: !!character.result,
      resultKeys: character.result ? Object.keys(character.result) : [],
      speechPattern: character.result?.speechPattern,
      speechStyle: character.result?.speechStyle,
      behavior: character.result?.behavior,
      behaviorFirstPerson: character.result?.behavior?.firstPerson,
      behaviorSpeechPattern: character.result?.behavior?.speechPattern,
      sampleDialogues: character.result?.sampleDialogues,
      fullCharacterData: character.result
    })
    
    const participant: DialogueParticipant = {
      characterId: character.id,
      characterName: character.result?.name || character.result?.basicInfo?.name || character.title || 'Unknown',
      characterData: character.result || character,
      role: mode === 'roleplay' && dialogueStore.currentParticipants.length === 0 ? 'player' : 'npc',
      icon: character.result?.appearance?.icon || character.result?.basicInfo?.icon || character.icon
    }
    
    console.log('Created participant:', {
      id: participant.characterId,
      name: participant.characterName,
      role: participant.role,
      hasData: !!participant.characterData
    })
    dialogueStore.addParticipant(participant)
    setShowCharacterPicker(false)
    
    // 既存キャラとの関係性を自動分析（現在未実装のためコメントアウト）
    // TODO: analyzeRelationship APIが実装されたら有効化
    /*
    if (dialogueStore.currentParticipants.length > 0) {
      dialogueStore.currentParticipants.forEach(async (existing) => {
        await analyzeRelationship({
          char1: existing.characterData,
          char2: character.result || character
        })
      })
    }
    */
  }
  
  // モブキャラクター追加
  const handleAddMobCharacter = () => {
    if (!mobCharacter.name.trim()) {
      toast.error('モブキャラクターの名前を入力してください')
      return
    }
    
    const mobId = `mob_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
    const participant: DialogueParticipant = {
      characterId: mobId,
      characterName: mobCharacter.name,
      characterData: undefined,
      role: 'npc',
      isMob: true,
      mobRole: mobCharacter.role || 'その他',
      mobDescription: mobCharacter.description || `${mobCharacter.role || 'その他'}のキャラクター`
    }
    
    console.log('Adding mob character:', participant)
    
    dialogueStore.addParticipant(participant)
    setMobCharacter({ name: '', role: '', description: '' })
    setShowMobDialog(false)
    toast.success(`モブキャラクター「${mobCharacter.name}」を追加しました`)
  }

  // シーン設定を適用
  const applySceneSettings = () => {
    dialogueStore.setScene(sceneSetting)
    toast.success('シーン設定を適用しました')
    // 設定適用後、対話タブに切り替える（ユーザーにフィードバックを与える）
    if (dialogueStore.currentParticipants.length >= 2) {
      setActiveTab('dialogue')
    }
  }
  
  // プリセット保存
  const handleSavePreset = () => {
    if (!presetForm.name.trim()) {
      toast.error('プリセット名を入力してください')
      return
    }
    
    // currentSceneまたはフォームの入力値を使用
    const sceneToSave = dialogueStore.currentScene || sceneSetting
    
    if (!sceneToSave.location || !sceneToSave.situation || !sceneToSave.mood) {
      toast.error('場所、状況、雰囲気を入力してください')
      return
    }
    
    const tags = presetForm.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0)
    
    // 一時的にcurrentSceneを設定してから保存（saveScenePresetがcurrentSceneを使用するため）
    const originalScene = dialogueStore.currentScene
    dialogueStore.setScene(sceneToSave)
    
    dialogueStore.saveScenePreset(
      presetForm.name,
      presetForm.description || undefined,
      tags.length > 0 ? tags : undefined
    )
    
    // 元のcurrentSceneに戻す（nullの場合も含む）
    if (originalScene) {
      dialogueStore.setScene(originalScene)
    } else {
      // currentSceneをリセット
      dialogueStore.setScene(null as any)
    }
    
    setPresetForm({ name: '', description: '', tags: '' })
    setShowPresetDialog(false)
    toast.success('プリセットを保存しました')
  }
  
  // プリセット読み込み
  const handleLoadPreset = (presetId: string) => {
    dialogueStore.loadScenePreset(presetId)
    const preset = dialogueStore.scenePresets.find(p => p.id === presetId)
    if (preset) {
      setSceneSetting(preset.settings)
      setShowPresetList(false)
      toast.success(`プリセット「${preset.name}」を読み込みました`)
    }
  }


  // 対話生成
  const handleGenerate = async () => {
    if (!dialogueStore.currentScene) {
      toast.error('シーン設定を先に行ってください')
      return
    }
    
    if (dialogueStore.currentParticipants.length < 2) {
      toast.error('最低2人のキャラクターが必要です')
      return
    }

    try {
      if (mode === 'roleplay') {
        if (!playerMessage) {
          toast.error('メッセージを入力してください')
          return
        }
        
        const playerChar = dialogueStore.currentParticipants.find(p => p.role === 'player')
        if (!playerChar) {
          toast.error('プレイヤーキャラクターを選択してください')
          return
        }

        console.log('Generating roleplay with:', {
          participants: dialogueStore.currentParticipants.length,
          hasActiveSimulation: !!dialogueStore.activeSimulation,
          dialogueHistory: dialogueStore.activeSimulation?.result.dialogues?.length || 0,
          participantDetails: dialogueStore.currentParticipants.map(p => ({
            id: p.characterId,
            name: p.characterName,
            role: p.role,
            hasData: !!p.characterData
          }))
        })

        console.log('[DialogueSimulator] Roleplay generation with context:', {
          hasWorldContext: !!generatorContext.worldContext,
          charactersCount: generatorContext.characters?.length || 0,
          itemsCount: generatorContext.items?.length || 0,
          hasCustomContext: !!generatorContext.customContext
        })

        const result = await generateRoleplay({
          mode: 'roleplay',
          participants: dialogueStore.currentParticipants,
          playerCharacterId: playerChar.characterId,
          playerMessage,
          sceneSettings: dialogueStore.currentScene,
          relationships: dialogueStore.currentRelationships,
          dialogueHistory: dialogueStore.activeSimulation?.result.dialogues || [],
          context: generatorContext,  // GeneratorContextからのコンテキスト
          useGemini3Mode,  // Gemini 3.0モード
          useGemini3FlashMode,  // Gemini 3.0 Flashモード
          options: {
            responseLength: dialogueOptions.responseLength,
            includeActions: dialogueOptions.includeActions,
            includeThoughts: dialogueOptions.includeThoughts,
            emotionalIntensity: dialogueOptions.emotionalIntensity
          }
        })
        
        console.log('Roleplay result:', result)
        console.log('Active simulation after generation:', dialogueStore.activeSimulation)
        
        setPlayerMessage('')
      } else {
        console.log('Generating auto dialogue with:', {
          participants: dialogueStore.currentParticipants.length,
          turns: autoTurns,
          participantDetails: dialogueStore.currentParticipants.map(p => ({
            id: p.characterId,
            name: p.characterName,
            hasData: !!p.characterData
          }))
        })
        
        console.log('[DialogueSimulator] Auto generation with context:', {
          hasWorldContext: !!generatorContext.worldContext,
          charactersCount: generatorContext.characters?.length || 0,
          itemsCount: generatorContext.items?.length || 0,
          hasCustomContext: !!generatorContext.customContext
        })

        const result = await generateAuto({
          mode: 'auto',
          participants: dialogueStore.currentParticipants,
          sceneSettings: dialogueStore.currentScene,
          relationships: dialogueStore.currentRelationships,
          dialogueLength: autoTurns,
          startingPrompt: playerMessage || undefined,
          context: generatorContext,  // GeneratorContextからのコンテキスト
          useGemini3Mode,  // Gemini 3.0モード
          useGemini3FlashMode,  // Gemini 3.0 Flashモード
          options: {
            responseLength: dialogueOptions.responseLength,
            includeActions: dialogueOptions.includeActions,
            includeThoughts: dialogueOptions.includeThoughts,
            emotionalIntensity: dialogueOptions.emotionalIntensity,
            naturalFlow: dialogueOptions.naturalFlow,
            conflictLevel: dialogueOptions.conflictLevel,
            humorLevel: dialogueOptions.humorLevel,
            dramaticTension: dialogueOptions.dramaticTension
          }
        })
        
        console.log('Auto result:', result)
        console.log('Active simulation after generation:', dialogueStore.activeSimulation)
      }
      
      // 生成後の状態を確認
      setTimeout(() => {
        console.log('Active simulation 100ms after generation:', dialogueStore.activeSimulation)
        console.log('Simulations count:', dialogueStore.simulations.length)
      }, 100)
      
      setActiveTab('dialogue')
      toast.success('対話を生成しました')
    } catch (error: any) {
      console.error('Generation error:', error)
      toast.error(error.message || '対話生成中にエラーが発生しました')
    }
  }

  // 続き生成
  const handleContinue = async (newSceneSettings?: SceneSettings, sceneTransition?: string) => {
    if (!dialogueStore.activeSimulation) return

    // デバッグ：現在のストアの状態を確認
    console.log('Handle continue - Store state:', {
      currentParticipants: dialogueStore.currentParticipants,
      currentParticipantsCount: dialogueStore.currentParticipants?.length,
      currentParticipantsData: dialogueStore.currentParticipants?.map(p => ({
        name: p.characterName,
        hasData: !!p.characterData
      })),
      currentScene: dialogueStore.currentScene,
      currentRelationships: dialogueStore.currentRelationships,
      activeSimulationParticipants: dialogueStore.activeSimulation?.result?.participants,
      activeSimulationScene: dialogueStore.activeSimulation?.result?.sceneSettings
    })

    // シーン変更がある場合はストアを更新
    if (newSceneSettings) {
      dialogueStore.updateSceneSettings(dialogueStore.activeSimulation.id, newSceneSettings)
      setSceneSetting(newSceneSettings)
    }

    try {
      await continueDialogue({
        simulationId: dialogueStore.activeSimulation.id,
        turns: mode === 'auto' ? autoTurns : 1,
        context: generatorContext,  // コンテキストを追加
        useGemini3Mode,  // Gemini 3.0モード
        useGemini3FlashMode,  // Gemini 3.0 Flashモード
        options: {
          responseLength: dialogueOptions.responseLength,
          includeActions: dialogueOptions.includeActions,
          includeThoughts: dialogueOptions.includeThoughts,
          emotionalIntensity: dialogueOptions.emotionalIntensity,
          naturalFlow: dialogueOptions.naturalFlow,
          conflictLevel: dialogueOptions.conflictLevel,
          humorLevel: dialogueOptions.humorLevel,
          dramaticTension: dialogueOptions.dramaticTension,
          sceneTransition // シーン遷移の説明を追加
        }
      })
      toast.success(sceneTransition ? 'シーンを変更して続きを生成しました' : '続きを生成しました')
    } catch (error: any) {
      console.error('Continue error:', error)
      toast.error(error.message || '続き生成中にエラーが発生しました')
    }
  }
  
  // 履歴圧縮
  const handleCompressHistory = async () => {
    if (!dialogueStore.activeSimulation) return
    
    if (dialogueStore.activeSimulation.result.dialogues.length < 20) {
      toast.error('圧縮するには最低20メッセージ必要です')
      return
    }
    
    try {
      await compressHistory({
        simulationId: dialogueStore.activeSimulation.id,
        keepRecentCount: 10
      })
      toast.success('履歴を圧縮しました')
    } catch (error: any) {
      console.error('Compress error:', error)
      toast.error(error.message || '履歴圧縮中にエラーが発生しました')
    }
  }

  // メッセージ編集
  const handleEditMessage = (turnId: string, content: string) => {
    if (!dialogueStore.activeSimulation) return
    dialogueStore.editDialogueTurn(dialogueStore.activeSimulation.id, turnId, content)
    setEditingTurnId(null)
    setEditContent('')
  }

  // メッセージ削除
  const handleDeleteMessage = (turnId: string) => {
    if (!dialogueStore.activeSimulation) return
    dialogueStore.deleteDialogueTurn(dialogueStore.activeSimulation.id, turnId)
  }

  return (
    <div className="container mx-auto p-4 space-y-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MessageSquare className="w-6 h-6" />
          対話シミュレータ
        </h1>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowShortcuts(true)}
            title="キーボードショートカット"
          >
            <Keyboard className="w-4 h-4" />
          </Button>
          <Select value={mode} onValueChange={(v: 'roleplay' | 'auto') => setMode(v)}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="roleplay">ロールプレイモード</SelectItem>
              <SelectItem value="auto">自動生成モード</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* タブ */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="setup">
            <Settings className="w-4 h-4 mr-2" />
            設定
          </TabsTrigger>
          <TabsTrigger value="dialogue">
            <MessageSquare className="w-4 h-4 mr-2" />
            対話
          </TabsTrigger>
          <TabsTrigger value="history">
            <Users className="w-4 h-4 mr-2" />
            履歴
          </TabsTrigger>
        </TabsList>

        {/* 設定タブ */}
        <TabsContent value="setup" className="space-y-4">
          {/* ジェネレータ連携 */}
          <Card>
            <CardHeader>
              <CardTitle>他ジェネレータ連携</CardTitle>
            </CardHeader>
            <CardContent>
              <GeneratorContext
                onContextChange={setGeneratorContext}
                customContext={customContext}
                onCustomContextChange={setCustomContext}
                // stateをpropsとして渡す
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                primaryWorldId={primaryWorldId}
                setPrimaryWorldId={setPrimaryWorldId}
                secondaryWorldId={secondaryWorldId}
                setSecondaryWorldId={setSecondaryWorldId}
                dataMode={dataMode}
                setDataMode={setDataMode}
                selectedCharacterIds={selectedCharacterIds}
                setSelectedCharacterIds={setSelectedCharacterIds}
                selectedItemIds={selectedItemIds}
                setSelectedItemIds={setSelectedItemIds}
                selectedEnemyIds={selectedEnemyIds}
                setSelectedEnemyIds={setSelectedEnemyIds}
                selectedRelationshipIds={selectedRelationshipIds}
                setSelectedRelationshipIds={setSelectedRelationshipIds}
                selectedPlotIds={selectedPlotIds}
                setSelectedPlotIds={setSelectedPlotIds}
                selectedTitleIds={selectedTitleIds}
                setSelectedTitleIds={setSelectedTitleIds}
                selectedNameIds={selectedNameIds}
                setSelectedNameIds={setSelectedNameIds}
                selectedWikipediaIds={selectedWikipediaIds}
                setSelectedWikipediaIds={setSelectedWikipediaIds}
                selectedImagePromptIds={selectedImagePromptIds}
                setSelectedImagePromptIds={setSelectedImagePromptIds}
                selectedSkillIds={selectedSkillIds}
                setSelectedSkillIds={setSelectedSkillIds}
                selectedDungeonIds={selectedDungeonIds}
                setSelectedDungeonIds={setSelectedDungeonIds}
                selectedCommunityIds={selectedCommunityIds}
                setSelectedCommunityIds={setSelectedCommunityIds}
                selectedSocietyIds={selectedSocietyIds}
                setSelectedSocietyIds={setSelectedSocietyIds}
                selectedBuildingIds={selectedBuildingIds}
                setSelectedBuildingIds={setSelectedBuildingIds}
                selectedLocationIds={selectedLocationIds}
                setSelectedLocationIds={setSelectedLocationIds}
                selectedDocumentIds={selectedDocumentIds}
                setSelectedDocumentIds={setSelectedDocumentIds}
                selectedSummaryIds={selectedSummaryIds}
                setSelectedSummaryIds={setSelectedSummaryIds}
              />
            </CardContent>
          </Card>
          
          {/* 表示設定 */}
          <Card>
            <CardHeader>
              <CardTitle>表示設定</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-avatars"
                  checked={dialogueStore.displayOptions.showAvatars}
                  onCheckedChange={(checked) => 
                    dialogueStore.updateDisplayOptions({ showAvatars: !!checked })
                  }
                />
                <Label htmlFor="show-avatars">アバター表示</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-portraits"
                  checked={dialogueStore.displayOptions.showPortraits}
                  onCheckedChange={(checked) => 
                    dialogueStore.updateDisplayOptions({ showPortraits: !!checked })
                  }
                />
                <Label htmlFor="show-portraits">立ち絵表示</Label>
              </div>
              {dialogueStore.displayOptions.showPortraits && (
                <div className="space-y-2">
                  <Label htmlFor="portrait-position">立ち絵位置</Label>
                  <Select
                    value={dialogueStore.displayOptions.portraitPosition}
                    onValueChange={(value: 'left' | 'right' | 'floating') => 
                      dialogueStore.updateDisplayOptions({ portraitPosition: value })
                    }
                  >
                    <SelectTrigger id="portrait-position">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">左側</SelectItem>
                      <SelectItem value="right">右側</SelectItem>
                      <SelectItem value="floating">フローティング</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* 参加者設定 */}
          <Card>
            <CardHeader>
              <CardTitle>参加キャラクター</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                {dialogueStore.currentParticipants.map((p) => (
                  <Card key={p.characterId} className="p-3">
                    <div className="flex items-center gap-3">
                      {/* アバター */}
                      <div className={`w-12 h-12 rounded-full ${p.isMob ? 'bg-orange-200' : 'bg-gray-200'} flex items-center justify-center text-sm font-medium overflow-hidden`}>
                        {p.icon ? (
                          <img src={p.icon} alt={p.characterName} className="w-full h-full object-cover" />
                        ) : (
                          p.characterName[0]
                        )}
                      </div>
                      
                      {/* キャラクター情報 */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{p.characterName}</span>
                          {p.isMob && (
                            <Badge variant="outline" className="text-xs">
                              モブ: {p.mobRole}
                            </Badge>
                          )}
                          {mode === 'roleplay' && !p.isMob && (
                            <Badge variant={p.role === 'player' ? 'default' : 'secondary'}>
                              {p.role === 'player' ? 'プレイヤー' : 'NPC'}
                            </Badge>
                          )}
                          {mode === 'roleplay' && p.role === 'npc' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => dialogueStore.setPlayerCharacter(p.characterId)}
                            >
                              プレイヤーにする
                            </Button>
                          )}
                        </div>
                        
                        {/* 画像アップロードボタン */}
                        <div className="flex gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const input = document.createElement('input')
                              input.type = 'file'
                              input.accept = 'image/*'
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0]
                                if (file) {
                                  const reader = new FileReader()
                                  reader.onload = (event) => {
                                    const base64 = event.target?.result as string
                                    dialogueStore.updateParticipantIcon(p.characterId, base64)
                                    toast.success('アイコンを設定しました')
                                  }
                                  reader.readAsDataURL(file)
                                }
                              }
                              input.click()
                            }}
                          >
                            <Upload className="w-3 h-3 mr-1" />
                            アイコン
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const input = document.createElement('input')
                              input.type = 'file'
                              input.accept = 'image/*'
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0]
                                if (file) {
                                  const reader = new FileReader()
                                  reader.onload = (event) => {
                                    const base64 = event.target?.result as string
                                    dialogueStore.updateParticipantPortrait(p.characterId, base64)
                                    toast.success('立ち絵を設定しました')
                                  }
                                  reader.readAsDataURL(file)
                                }
                              }
                              input.click()
                            }}
                          >
                            <Image className="w-3 h-3 mr-1" />
                            立ち絵
                          </Button>
                          
                          {p.portrait && (
                            <Badge variant="secondary" className="text-xs">
                              立ち絵設定済
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* 削除ボタン */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => dialogueStore.removeParticipant(p.characterId)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Button onClick={() => setShowCharacterPicker(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  キャラクターを追加
                </Button>
                <Button variant="outline" onClick={() => setShowMobDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  モブキャラクター
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* シーン設定 */}
          <Card>
            <CardHeader>
              <CardTitle>シーン設定</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* 現在のシーン設定表示 */}
              {dialogueStore.currentScene && (
                <div className="p-3 bg-gray-50 rounded-lg mb-4">
                  <p className="text-sm font-medium mb-2">現在のシーン設定:</p>
                  <div className="text-sm space-y-1">
                    <p>📍 場所: {dialogueStore.currentScene.location || '未設定'}</p>
                    <p>🎭 状況: {dialogueStore.currentScene.situation || '未設定'}</p>
                    <p>💭 雰囲気: {dialogueStore.currentScene.mood || '未設定'}</p>
                    {dialogueStore.currentScene.timeOfDay && <p>🕐 時間帯: {dialogueStore.currentScene.timeOfDay}</p>}
                    {dialogueStore.currentScene.weather && <p>☁️ 天候: {dialogueStore.currentScene.weather}</p>}
                  </div>
                </div>
              )}
              <div>
                <Label>場所</Label>
                <Input
                  placeholder="例: 学校の屋上、深夜のバー"
                  value={sceneSetting.location}
                  onChange={(e) => setSceneSetting({...sceneSetting, location: e.target.value})}
                />
              </div>
              <div>
                <Label>状況</Label>
                <Input
                  placeholder="例: 偶然の再会、対決前"
                  value={sceneSetting.situation}
                  onChange={(e) => setSceneSetting({...sceneSetting, situation: e.target.value})}
                />
              </div>
              <div>
                <Label>雰囲気</Label>
                <Select
                  value={sceneSetting.mood}
                  onValueChange={(v) => setSceneSetting({...sceneSetting, mood: v as any})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="casual">カジュアル</SelectItem>
                    <SelectItem value="tense">緊張感</SelectItem>
                    <SelectItem value="romantic">ロマンチック</SelectItem>
                    <SelectItem value="comedic">コメディ</SelectItem>
                    <SelectItem value="serious">シリアス</SelectItem>
                    <SelectItem value="action">アクション</SelectItem>
                    <SelectItem value="mysterious">ミステリアス</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>時間帯（オプション）</Label>
                <Select
                  value={sceneSetting.timeOfDay || 'none'}
                  onValueChange={(v) => setSceneSetting({...sceneSetting, timeOfDay: v === 'none' ? undefined : v as any})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選択..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">未設定</SelectItem>
                    <SelectItem value="morning">朝</SelectItem>
                    <SelectItem value="afternoon">午後</SelectItem>
                    <SelectItem value="evening">夕方</SelectItem>
                    <SelectItem value="night">夜</SelectItem>
                    <SelectItem value="dawn">夜明け</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>天候（オプション）</Label>
                <Input
                  placeholder="例: 雨、雪、晴れ"
                  value={sceneSetting.weather || ''}
                  onChange={(e) => setSceneSetting({...sceneSetting, weather: e.target.value || undefined})}
                />
              </div>
              <div>
                <Label>背景情報（オプション）</Label>
                <Textarea
                  placeholder="シーンの背景や追加情報"
                  value={sceneSetting.backgroundInfo || ''}
                  onChange={(e) => setSceneSetting({...sceneSetting, backgroundInfo: e.target.value || undefined})}
                  rows={2}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={applySceneSettings}>
                  シーン設定を適用
                </Button>
                <Button variant="outline" onClick={() => setShowPresetList(true)}>
                  プリセット読込
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowPresetDialog(true)}
                  disabled={!sceneSetting.location || !sceneSetting.situation || !sceneSetting.mood}
                >
                  プリセット保存
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* 関係性設定 - キャラクターが2名以上いれば常に表示 */}
          {dialogueStore.currentParticipants.length >= 2 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  キャラクター関係性
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowAddRelationship(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    関係性を追加
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dialogueStore.currentRelationships.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      関係性が設定されていません。「関係性を追加」ボタンから追加してください。
                    </p>
                  ) : (
                    dialogueStore.currentRelationships.map((rel) => {
                    const char1 = dialogueStore.currentParticipants.find(p => p.characterId === rel.character1Id)
                    const char2 = dialogueStore.currentParticipants.find(p => p.characterId === rel.character2Id)
                    return (
                      <div key={`${rel.character1Id}_${rel.character2Id}`} className="flex items-center justify-between p-2 border rounded">
                        <span>{char1?.characterName} ⇔ {char2?.characterName}</span>
                        <div className="flex items-center gap-2">
                          <Badge>{rel.relationshipType}</Badge>
                          <Badge variant="secondary">親密度: {rel.friendshipLevel}</Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingRelationship({char1: rel.character1Id, char2: rel.character2Id})
                              setRelationshipForm(rel)
                            }}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    )
                  })
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* 詳細オプション設定 */}
          <Card>
            <CardHeader>
              <CardTitle>詳細オプション</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* 生成モード設定 */}
              <div className="p-3 border rounded-lg space-y-2">
                <Label className="text-sm font-medium">生成モード</Label>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="useGemini3Mode"
                    checked={useGemini3Mode}
                    onCheckedChange={(v) => {
                      setUseGemini3Mode(!!v)
                      if (v) setUseGemini3FlashMode(false)
                    }}
                  />
                  <Label htmlFor="useGemini3Mode" className="text-sm">
                    Gemini 3 モード（有料APIのみ・試験版）
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="useGemini3FlashMode"
                    checked={useGemini3FlashMode}
                    onCheckedChange={(v) => {
                      setUseGemini3FlashMode(!!v)
                      if (v) setUseGemini3Mode(false)
                    }}
                  />
                  <Label htmlFor="useGemini3FlashMode" className="text-sm">
                    Gemini 3 Flash モード（有料APIのみ・試験版・高速）
                  </Label>
                </div>
              </div>
              <div>
                <Label>応答の長さ</Label>
                <Select
                  value={dialogueOptions.responseLength}
                  onValueChange={(v) => setDialogueOptions({...dialogueOptions, responseLength: v as any})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short">短め</SelectItem>
                    <SelectItem value="medium">標準</SelectItem>
                    <SelectItem value="long">長め</SelectItem>
                    <SelectItem value="none">指定なし</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeActions"
                  checked={dialogueOptions.includeActions}
                  onCheckedChange={(v) => setDialogueOptions({...dialogueOptions, includeActions: !!v})}
                />
                <Label htmlFor="includeActions">動作描写を含む</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeThoughts"
                  checked={dialogueOptions.includeThoughts}
                  onCheckedChange={(v) => setDialogueOptions({...dialogueOptions, includeThoughts: !!v})}
                />
                <Label htmlFor="includeThoughts">内心描写を含む</Label>
              </div>
              <div>
                <Label>感情の強度 ({dialogueOptions.emotionalIntensity})</Label>
                <Slider
                  value={[dialogueOptions.emotionalIntensity]}
                  onValueChange={(v) => setDialogueOptions({...dialogueOptions, emotionalIntensity: v[0]})}
                  min={1}
                  max={10}
                  step={1}
                />
              </div>
              {mode === 'auto' && (
                <>
                  <div>
                    <Label>衝突レベル ({dialogueOptions.conflictLevel})</Label>
                    <Slider
                      value={[dialogueOptions.conflictLevel]}
                      onValueChange={(v) => setDialogueOptions({...dialogueOptions, conflictLevel: v[0]})}
                      min={0}
                      max={10}
                      step={1}
                    />
                  </div>
                  <div>
                    <Label>ユーモアレベル ({dialogueOptions.humorLevel})</Label>
                    <Slider
                      value={[dialogueOptions.humorLevel]}
                      onValueChange={(v) => setDialogueOptions({...dialogueOptions, humorLevel: v[0]})}
                      min={0}
                      max={10}
                      step={1}
                    />
                  </div>
                  <div>
                    <Label>ドラマチック度 ({dialogueOptions.dramaticTension})</Label>
                    <Slider
                      value={[dialogueOptions.dramaticTension]}
                      onValueChange={(v) => setDialogueOptions({...dialogueOptions, dramaticTension: v[0]})}
                      min={0}
                      max={10}
                      step={1}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 対話タブ */}
        <TabsContent value="dialogue" className="space-y-4">
          {!dialogueStore.activeSimulation ? (
            <Card>
              <CardContent className="py-8">
                <div className="text-center text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="mb-2">対話がまだ開始されていません</p>
                  <div className="text-sm space-y-2">
                    <p>対話を開始するには：</p>
                    <ol className="text-left max-w-sm mx-auto space-y-1">
                      <li>1. セットアップタブでキャラクターを追加（2名以上）</li>
                      <li>2. シーン設定を適用</li>
                      <li>3. 必要に応じて関係性を設定</li>
                      <li>4. この下の入力欄から対話を開始</li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* 対話履歴と立ち絵 */}
              <div className={`flex gap-4 ${dialogueStore.displayOptions.showPortraits && dialogueStore.displayOptions.portraitPosition !== 'floating' ? 'relative' : ''}`}>
                {/* 左側立ち絵 */}
                {dialogueStore.displayOptions.showPortraits && dialogueStore.displayOptions.portraitPosition === 'left' && (
                  <div className="w-48">
                    {dialogueStore.activeSimulation.result.dialogues.length > 0 && (() => {
                      const lastTurn = dialogueStore.activeSimulation.result.dialogues[dialogueStore.activeSimulation.result.dialogues.length - 1]
                      const participant = dialogueStore.currentParticipants.find(p => p.characterId === lastTurn.characterId)
                      const portrait = participant?.portrait || dialogueStore.characterPortraitCache[lastTurn.characterId]
                      return portrait ? (
                        <div className="sticky top-0">
                          <img src={portrait} alt={lastTurn.characterName} className="w-full h-auto rounded-lg shadow-lg" />
                          <p className="text-center mt-2 font-semibold">{lastTurn.characterName}</p>
                        </div>
                      ) : null
                    })()}
                  </div>
                )}
                
                {/* 対話履歴カード */}
                <Card className="flex-1">
                  <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{dialogueStore.activeSimulation.title}</CardTitle>
                    {dialogueStore.activeSimulation.result.dialogues.length >= 20 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCompressHistory}
                        disabled={isCompressing}
                      >
                        履歴を圧縮
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] w-full">
                    <div className="space-y-4">
                      {dialogueStore.activeSimulation.result.dialogues.map((turn) => {
                        const participant = dialogueStore.currentParticipants.find(p => p.characterId === turn.characterId)
                        const icon = participant?.icon || dialogueStore.characterIconCache[turn.characterId]
                        const portrait = participant?.portrait || dialogueStore.characterPortraitCache[turn.characterId]
                        return (
                          <div key={turn.id} className="flex gap-3">
                            {/* アバター表示 */}
                            {dialogueStore.displayOptions.showAvatars && (
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium overflow-hidden">
                                {icon ? (
                                  <img src={icon} alt={turn.characterName} className="w-full h-full object-cover" />
                                ) : (
                                  turn.characterName[0]
                                )}
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold">{turn.characterName}</span>
                                {turn.emotion && <Badge variant="outline">{turn.emotion}</Badge>}
                                <div className="ml-auto flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setEditingTurnId(turn.id)
                                      setEditContent(turn.content)
                                    }}
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteMessage(turn.id)}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                              {editingTurnId === turn.id ? (
                                <div className="flex gap-2">
                                  <Input
                                    value={editContent}
                                    onChange={(e) => setEditContent(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleEditMessage(turn.id, editContent)
                                      }
                                    }}
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => handleEditMessage(turn.id, editContent)}
                                  >
                                    保存
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setEditingTurnId(null)
                                      setEditContent('')
                                    }}
                                  >
                                    キャンセル
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <p className="text-sm">{turn.content}</p>
                                  {turn.action && (
                                    <p className="text-xs text-muted-foreground italic">
                                      [{turn.action}]
                                    </p>
                                  )}
                                  {turn.thought && (
                                    <p className="text-xs text-muted-foreground italic">
                                      ({turn.thought})
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </ScrollArea>
                </CardContent>
                </Card>
                
                {/* 右側立ち絵 */}
                {dialogueStore.displayOptions.showPortraits && dialogueStore.displayOptions.portraitPosition === 'right' && (
                  <div className="w-48">
                    {dialogueStore.activeSimulation.result.dialogues.length > 0 && (() => {
                      const lastTurn = dialogueStore.activeSimulation.result.dialogues[dialogueStore.activeSimulation.result.dialogues.length - 1]
                      const participant = dialogueStore.currentParticipants.find(p => p.characterId === lastTurn.characterId)
                      const portrait = participant?.portrait || dialogueStore.characterPortraitCache[lastTurn.characterId]
                      return portrait ? (
                        <div className="sticky top-0">
                          <img src={portrait} alt={lastTurn.characterName} className="w-full h-auto rounded-lg shadow-lg" />
                          <p className="text-center mt-2 font-semibold">{lastTurn.characterName}</p>
                        </div>
                      ) : null
                    })()}
                  </div>
                )}
              </div>
              
              {/* フローティング立ち絵 */}
              {dialogueStore.displayOptions.showPortraits && dialogueStore.displayOptions.portraitPosition === 'floating' && (
                <div className="fixed bottom-4 right-4 z-50">
                  {dialogueStore.activeSimulation.result.dialogues.length > 0 && (() => {
                    const lastTurn = dialogueStore.activeSimulation.result.dialogues[dialogueStore.activeSimulation.result.dialogues.length - 1]
                    const participant = dialogueStore.currentParticipants.find(p => p.characterId === lastTurn.characterId)
                    const portrait = participant?.portrait || dialogueStore.characterPortraitCache[lastTurn.characterId]
                    return portrait ? (
                      <div className="bg-white rounded-lg shadow-xl p-2">
                        <img src={portrait} alt={lastTurn.characterName} className="w-32 h-auto rounded" />
                        <p className="text-center text-sm mt-1 font-semibold">{lastTurn.characterName}</p>
                      </div>
                    ) : null
                  })()}
                </div>
              )}
            </>
          )}
          
          {/* 入力エリア（常に表示） */}
          <Card>
            <CardContent className="pt-6">
              {mode === 'roleplay' ? (
                <div className="flex gap-2">
                  <Textarea
                    placeholder="メッセージを入力..."
                    value={playerMessage}
                    onChange={(e) => setPlayerMessage(e.target.value)}
                    className="flex-1"
                    rows={3}
                  />
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={handleGenerate}
                      disabled={isGenerating || !playerMessage || dialogueStore.currentParticipants.length < 2}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      送信
                    </Button>
                    {dialogueStore.activeSimulation && (
                      <Button
                        variant="secondary"
                        onClick={() => handleContinue()}
                        disabled={isContinuing || !dialogueStore.activeSimulation?.result.dialogues.length}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        続き
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label>開始のきっかけ（オプション）</Label>
                    <Input
                      placeholder="例: AがBに声をかける"
                      value={playerMessage}
                      onChange={(e) => setPlayerMessage(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>生成する発言数（1発言 = 1人が1回話す）</Label>
                    <Slider
                      value={[autoTurns]}
                      onValueChange={(v) => setAutoTurns(v[0])}
                      min={1}
                      max={20}
                      step={1}
                    />
                    <span className="text-sm text-muted-foreground">{autoTurns}個の発言</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleGenerate}
                      disabled={isGenerating || dialogueStore.currentParticipants.length < 2}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      生成開始
                    </Button>
                    {dialogueStore.activeSimulation && (
                      <>
                        <Button
                          variant="secondary"
                          onClick={() => handleContinue()}
                          disabled={isContinuing || !dialogueStore.activeSimulation?.result.dialogues.length}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          続きを生成
                        </Button>
                        <Button
                          onClick={() => setShowSceneChangeModal(true)}
                          variant="outline"
                          size="sm"
                          disabled={!dialogueStore.activeSimulation || isGenerating || isContinuing}
                        >
                          <MapPin className="w-4 h-4 mr-2" />
                          シーン変更
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* エラー表示 */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error.message || error}</AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* 履歴タブ */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>シミュレーション履歴</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {dialogueStore.simulations.map((sim) => (
                  <div key={sim.id} className="flex items-center justify-between p-3 border rounded hover:bg-muted/50">
                    <div className="flex-1">
                      <h4 className="font-medium">{sim.title}</h4>
                      <p className="text-sm text-muted-foreground">
                        {new Date(sim.createdAt).toLocaleString()} · {sim.result.dialogues.length}メッセージ
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={sim.isFavorite ? "default" : "outline"}
                        onClick={() => dialogueStore.toggleFavorite(sim.id)}
                      >
                        <Star className={`w-4 h-4 ${sim.isFavorite ? 'fill-current' : ''}`} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          console.log('Loading simulation:', sim.id)
                          console.log('Simulation data:', {
                            participants: sim.result?.participants,
                            scene: sim.result?.sceneSettings,
                            relationships: sim.result?.relationships
                          })
                          dialogueStore.loadSimulation(sim.id)
                          // 読み込み後の状態を確認
                          setTimeout(() => {
                            console.log('After loadSimulation:', {
                              currentParticipants: dialogueStore.currentParticipants,
                              currentScene: dialogueStore.currentScene,
                              currentRelationships: dialogueStore.currentRelationships
                            })
                          }, 100)
                        }}
                      >
                        読み込む
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => dialogueStore.exportSimulation(sim.id, 'text')}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => dialogueStore.deleteSimulation(sim.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* キャラクター選択モーダル */}
      {showCharacterPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto">
            <CardHeader>
              <CardTitle>キャラクターを選択</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {characterStore.characters.map((char) => {
                  const isAdded = dialogueStore.currentParticipants.some(p => p.characterId === char.id)
                  return (
                    <Button
                      key={char.id}
                      variant={isAdded ? 'secondary' : 'outline'}
                      disabled={isAdded}
                      onClick={() => handleAddCharacter(char)}
                      className="justify-start"
                    >
                      {char.result?.name || char.title}
                    </Button>
                  )
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <Button variant="ghost" onClick={() => setShowCharacterPicker(false)}>
                  閉じる
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* モブキャラクター追加ダイアログ */}
      {showMobDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>モブキャラクターを追加</CardTitle>
              <p className="text-sm text-muted-foreground">
                簡単な設定だけで会話に参加できるキャラクターを追加できます
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>名前 *</Label>
                <Input
                  placeholder="例: 店員A、山賊、クラスメイトの田中"
                  value={mobCharacter.name}
                  onChange={(e) => setMobCharacter({...mobCharacter, name: e.target.value})}
                />
              </div>
              <div>
                <Label>役割・タイプ</Label>
                <Select
                  value={mobCharacter.role}
                  onValueChange={(v) => setMobCharacter({...mobCharacter, role: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="役割を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="クラスメイト">クラスメイト</SelectItem>
                    <SelectItem value="通行人">通行人</SelectItem>
                    <SelectItem value="店員">店員</SelectItem>
                    <SelectItem value="モンスター">モンスター</SelectItem>
                    <SelectItem value="山賊">山賊</SelectItem>
                    <SelectItem value="冒険者">冒険者</SelectItem>
                    <SelectItem value="村人">村人</SelectItem>
                    <SelectItem value="兵士">兵士</SelectItem>
                    <SelectItem value="貴族">貴族</SelectItem>
                    <SelectItem value="その他">その他</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>簡単な説明（任意）</Label>
                <Input
                  placeholder="例: 気さくな性格、いつも笑顔、少し怖がり"
                  value={mobCharacter.description}
                  onChange={(e) => setMobCharacter({...mobCharacter, description: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => {
                  setShowMobDialog(false)
                  setMobCharacter({ name: '', role: '', description: '' })
                }}>
                  キャンセル
                </Button>
                <Button onClick={handleAddMobCharacter}>
                  追加
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* プリセット保存ダイアログ */}
      {showPresetDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>シーン設定プリセットを保存</CardTitle>
              <p className="text-sm text-muted-foreground">
                現在のシーン設定をプリセットとして保存します
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>プリセット名 *</Label>
                <Input
                  placeholder="例: 学校の放課後、冒険者ギルド、深夜の酒場"
                  value={presetForm.name}
                  onChange={(e) => setPresetForm({...presetForm, name: e.target.value})}
                />
              </div>
              <div>
                <Label>説明（任意）</Label>
                <Input
                  placeholder="例: 放課後の教室で友人と雑談するシーン"
                  value={presetForm.description}
                  onChange={(e) => setPresetForm({...presetForm, description: e.target.value})}
                />
              </div>
              <div>
                <Label>タグ（任意・カンマ区切り）</Label>
                <Input
                  placeholder="例: 学園, 日常, 青春"
                  value={presetForm.tags}
                  onChange={(e) => setPresetForm({...presetForm, tags: e.target.value})}
                />
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium mb-2">保存される設定:</p>
                <div className="text-sm space-y-1">
                  <p>📍 場所: {(dialogueStore.currentScene || sceneSetting).location || '未設定'}</p>
                  <p>🎭 状況: {(dialogueStore.currentScene || sceneSetting).situation || '未設定'}</p>
                  <p>💭 雰囲気: {(dialogueStore.currentScene || sceneSetting).mood || '未設定'}</p>
                  {(dialogueStore.currentScene || sceneSetting).timeOfDay && <p>🕐 時間帯: {(dialogueStore.currentScene || sceneSetting).timeOfDay}</p>}
                  {(dialogueStore.currentScene || sceneSetting).weather && <p>☁️ 天候: {(dialogueStore.currentScene || sceneSetting).weather}</p>}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => {
                  setShowPresetDialog(false)
                  setPresetForm({ name: '', description: '', tags: '' })
                }}>
                  キャンセル
                </Button>
                <Button onClick={handleSavePreset}>
                  保存
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* プリセット一覧 */}
      {showPresetList && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto">
            <CardHeader>
              <CardTitle>シーン設定プリセット</CardTitle>
              <p className="text-sm text-muted-foreground">
                保存済みのプリセットを選択してください
              </p>
            </CardHeader>
            <CardContent>
              {dialogueStore.scenePresets.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  保存されたプリセットがありません
                </p>
              ) : (
                <div className="space-y-2">
                  {dialogueStore.scenePresets.map((preset) => (
                    <div 
                      key={preset.id} 
                      className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleLoadPreset(preset.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium">{preset.name}</h4>
                          {preset.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {preset.description}
                            </p>
                          )}
                          <div className="text-sm mt-2 space-y-1">
                            <p>📍 {preset.settings.location}</p>
                            <p>🎭 {preset.settings.situation}</p>
                            <p>💭 {preset.settings.mood}</p>
                          </div>
                          {preset.tags && preset.tags.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {preset.tags.map((tag, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation()
                            dialogueStore.deleteScenePreset(preset.id)
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <Button variant="ghost" onClick={() => setShowPresetList(false)}>
                  閉じる
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* キーボードショートカットヘルプ */}
      {showShortcuts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>キーボードショートカット</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="font-mono">Ctrl/Cmd + Enter</span>
                  <span className="text-muted-foreground">送信/生成</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-mono">Ctrl/Cmd + Shift + C</span>
                  <span className="text-muted-foreground">続きを生成</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-mono">Ctrl/Cmd + E</span>
                  <span className="text-muted-foreground">エクスポート</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-mono">Tab</span>
                  <span className="text-muted-foreground">タブ切り替え</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-mono">Enter</span>
                  <span className="text-muted-foreground">編集中メッセージを保存</span>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button variant="ghost" onClick={() => setShowShortcuts(false)}>
                  閉じる
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* 関係性編集モーダル */}
      {editingRelationship && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>関係性を編集</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>関係性タイプ</Label>
                <Select
                  value={relationshipForm.relationshipType || 'acquaintance'}
                  onValueChange={(v) => setRelationshipForm({...relationshipForm, relationshipType: v as CharacterRelationship['relationshipType']})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="family">家族</SelectItem>
                    <SelectItem value="friend">友人</SelectItem>
                    <SelectItem value="lover">恋人</SelectItem>
                    <SelectItem value="rival">ライバル</SelectItem>
                    <SelectItem value="enemy">敵</SelectItem>
                    <SelectItem value="colleague">同僚</SelectItem>
                    <SelectItem value="mentor">師弟</SelectItem>
                    <SelectItem value="acquaintance">知人</SelectItem>
                    <SelectItem value="stranger">初対面</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>親密度 ({relationshipForm.friendshipLevel || 0})</Label>
                <Slider
                  value={[relationshipForm.friendshipLevel || 0]}
                  onValueChange={(v) => setRelationshipForm({...relationshipForm, friendshipLevel: v[0]})}
                  min={-100}
                  max={100}
                  step={5}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>敵対的</span>
                  <span>中立</span>
                  <span>友好的</span>
                </div>
              </div>
              <div>
                <Label>過去の出来事</Label>
                <Textarea
                  placeholder="二人の間に起きた重要な出来事..."
                  value={relationshipForm.history || ''}
                  onChange={(e) => setRelationshipForm({...relationshipForm, history: e.target.value})}
                  rows={3}
                />
              </div>
              <div>
                <Label>追加設定</Label>
                <Textarea
                  placeholder="その他の関係性に関する詳細..."
                  value={relationshipForm.history || ''}
                  onChange={(e) => setRelationshipForm({...relationshipForm, history: e.target.value})}
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setEditingRelationship(null)
                    setRelationshipForm({})
                  }}
                >
                  キャンセル
                </Button>
                <Button
                  onClick={() => {
                    if (editingRelationship) {
                      dialogueStore.updateRelationship(
                        editingRelationship.char1,
                        editingRelationship.char2,
                        relationshipForm as CharacterRelationship
                      )
                      setEditingRelationship(null)
                      setRelationshipForm({})
                      toast.success('関係性を更新しました')
                    }
                  }}
                >
                  保存
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* 関係性追加モーダル */}
      {showAddRelationship && dialogueStore.currentParticipants.length >= 2 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>関係性を追加</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>キャラクター1</Label>
                <Select
                  value={relationshipForm.character1Id || ''}
                  onValueChange={(v) => setRelationshipForm({...relationshipForm, character1Id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選択..." />
                  </SelectTrigger>
                  <SelectContent>
                    {dialogueStore.currentParticipants.map(p => (
                      <SelectItem key={p.characterId} value={p.characterId}>
                        {p.characterName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>キャラクター2</Label>
                <Select
                  value={relationshipForm.character2Id || ''}
                  onValueChange={(v) => setRelationshipForm({...relationshipForm, character2Id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選択..." />
                  </SelectTrigger>
                  <SelectContent>
                    {dialogueStore.currentParticipants
                      .filter(p => p.characterId !== relationshipForm.character1Id)
                      .map(p => (
                        <SelectItem key={p.characterId} value={p.characterId}>
                          {p.characterName}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>関係性タイプ</Label>
                <Select
                  value={relationshipForm.relationshipType || 'stranger'}
                  onValueChange={(v) => setRelationshipForm({...relationshipForm, relationshipType: v as CharacterRelationship['relationshipType']})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="family">家族</SelectItem>
                    <SelectItem value="friend">友人</SelectItem>
                    <SelectItem value="lover">恋人</SelectItem>
                    <SelectItem value="rival">ライバル</SelectItem>
                    <SelectItem value="enemy">敵</SelectItem>
                    <SelectItem value="colleague">同僚</SelectItem>
                    <SelectItem value="mentor">師弟</SelectItem>
                    <SelectItem value="stranger">初対面</SelectItem>
                    <SelectItem value="custom">カスタム</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>親密度 ({relationshipForm.friendshipLevel || 0})</Label>
                <Slider
                  value={[relationshipForm.friendshipLevel || 0]}
                  onValueChange={(v) => setRelationshipForm({...relationshipForm, friendshipLevel: v[0]})}
                  min={-100}
                  max={100}
                  step={5}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>敵対的</span>
                  <span>中立</span>
                  <span>友好的</span>
                </div>
              </div>
              <div>
                <Label>過去の出来事</Label>
                <Textarea
                  placeholder="二人の間に起きた重要な出来事..."
                  value={relationshipForm.history || ''}
                  onChange={(e) => setRelationshipForm({...relationshipForm, history: e.target.value})}
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowAddRelationship(false)
                    setRelationshipForm({})
                  }}
                >
                  キャンセル
                </Button>
                <Button
                  onClick={() => {
                    if (relationshipForm.character1Id && relationshipForm.character2Id) {
                      const newRelationship: CharacterRelationship = {
                        character1Id: relationshipForm.character1Id,
                        character2Id: relationshipForm.character2Id,
                        relationshipType: relationshipForm.relationshipType as CharacterRelationship['relationshipType'] || 'stranger',
                        friendshipLevel: relationshipForm.friendshipLevel || 0,
                        history: relationshipForm.history
                      }
                      dialogueStore.setRelationship(newRelationship)
                      setShowAddRelationship(false)
                      setRelationshipForm({})
                      toast.success('関係性を追加しました')
                    } else {
                      toast.error('両方のキャラクターを選択してください')
                    }
                  }}
                >
                  追加
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* シーン変更モーダル */}
      {showSceneChangeModal && (
        <SceneChangeModal
          open={showSceneChangeModal}
          onClose={() => setShowSceneChangeModal(false)}
          onConfirm={(newScene, transition) => {
            setShowSceneChangeModal(false)
            handleContinue(newScene, transition)
          }}
          currentScene={dialogueStore.activeSimulation?.result.sceneSettings || sceneSetting}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
        />
      )}
    </div>
  )
}